import { CCard, CCardBody, CCardFooter } from "@coreui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Box, ButtonBase, Chip, useTheme } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import DocumentDataCardHeader from "./DocuementDataCardHeader";
import { CodeComponent } from "src/reusable/Chat/AssistantMessage/AssistantText";
import { useImageArtifact } from "src/views/assistant/hooks/useImageArtifact";
import ChunkContent, {
  tryParseJsonContainer,
} from "src/views/assistant/components/sources/ChunkContent";
import {
  containsRenderableHtml,
  sanitizeHtml,
  hasMeaningfulContent,
  htmlContentStyles,
} from "src/views/assistant/utils/htmlContentUtils";

type DocumentDataCardProps = {
  __source_uid: string;
  chunk_data: string | Record<string, any>;
  chunk_metadata: any;
  chunk_create_date: string;
  chunk_id: number;
  sdr_id: number;
  sdr_create_date: string;
  sdr_data: any;
  datasetId?: number;
  searchText?: string;
  type: DocumentDataCardType;
  scrollAndView: boolean;
  index?: number;
  /** DOM id for citation scroll-targeting; defaults to ``source-${index}``. */
  domId?: string;
  pageNumberCallback: (pgNum: number) => void;
  allowAutoScroll?: boolean;
};

export type DocumentDataCardType = "search" | "view";

const COLLAPSED_MARKDOWN_THRESHOLD = 800;

const CollapsibleMarkdown = ({ text }: { text: string }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const isLongText = text.length > COLLAPSED_MARKDOWN_THRESHOLD;
  const collapsed = isLongText && !expanded;

  return (
    <Box>
      <Box
        sx={{
          ...htmlContentStyles,
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          borderLeft: `4px solid ${theme.palette.divider}`,
          pl: 2,
          pr: 1,
          py: 0.5,
          lineHeight: 1.65,
          fontSize: "0.9375rem",
          color: theme.palette.text.primary,
          position: "relative",
          maxHeight: collapsed ? 320 : "none",
          overflow: collapsed ? "hidden" : "visible",
          "&::after": collapsed
            ? {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 64,
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
                pointerEvents: "none",
              }
            : undefined,
        }}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            code: CodeComponent,
          }}
        >
          {text}
        </Markdown>
      </Box>
      {isLongText && (
        <ButtonBase
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((o) => !o);
          }}
          aria-expanded={expanded}
          sx={{
            mt: 1,
            color: theme.palette.primary.main,
            fontSize: "0.8125rem",
            fontWeight: 500,
            justifyContent: "flex-start",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </ButtonBase>
      )}
    </Box>
  );
};

const DocumentDataCard = ({
  chunk_data,
  chunk_metadata,
  sdr_id,
  sdr_data,
  datasetId,
  searchText,
  type,
  index,
  domId,
  scrollAndView,
  pageNumberCallback,
  allowAutoScroll = true,
}: DocumentDataCardProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const parseJSON = (jsonString: string) => {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return {};
    }
  };

  // Note: When using virtualized list (react-window) in "view" mode,
  // scrolling is handled by the parent via scrollToItem().
  // This scrollIntoView is only used for "search" mode.
  useEffect(() => {
    if (scrollAndView && allowAutoScroll && type === "search") {
      if (ref.current) {
        ref.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      }
    }
  }, [scrollAndView, allowAutoScroll, type]);

  // only parse if the data is not already an object
  const parsedChunkData =
    typeof chunk_data === "object" ? chunk_data : parseJSON(chunk_data);

  const parsedChunkMetadata =
    typeof chunk_metadata === "object"
      ? chunk_metadata
      : parseJSON(chunk_metadata);

  const parsedSDRData =
    typeof sdr_data === "object" ? sdr_data : parseJSON(sdr_data);

  const highlightStyles = scrollAndView
    ? {
        border: "2px solid #1976d2",
        backgroundColor: "#e3f2fd",
        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
      }
    : type === "search"
      ? {
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
            borderColor: "#bdbdbd",
          },
        }
      : {};

  let pageNumbers = Array.isArray(parsedChunkMetadata.page_number)
    ? parsedChunkMetadata.page_number
    : [parsedChunkMetadata.page_number];

  // Image artifact support for Epic OCR image chunks
  const imageArtifactId = parsedChunkMetadata.image_artifact_id
    ? Number(parsedChunkMetadata.image_artifact_id)
    : null;
  const { data: imageUrl } = useImageArtifact(imageArtifactId);

  // Route to <ChunkContent> when chunk_data.data is non-string OR a string
  // that's actually a JSON container (e.g. .xer chunks come through as
  // stringified JSON activity records). PDF text chunks remain on the
  // markdown render path below.
  const rawChunkData = parsedChunkData?.data;
  // Parse once; reuse for both the routing decision and ChunkContent so we
  // don't re-parse the same (potentially large) JSON string on every render.
  const parsedChunkContainer = useMemo(
    () =>
      typeof rawChunkData === "string"
        ? tryParseJsonContainer(rawChunkData)
        : undefined,
    [rawChunkData],
  );
  const isStructuredChunk =
    rawChunkData !== null &&
    rawChunkData !== undefined &&
    (typeof rawChunkData !== "string" || parsedChunkContainer !== undefined);

  const chunkDataText = typeof rawChunkData === "string" ? rawChunkData : "";

  // Determine if the content is HTML that should be rendered
  const { hasHtml, sanitizedContent } = useMemo(() => {
    if (!chunkDataText) {
      return { hasHtml: false, sanitizedContent: "" };
    }

    if (containsRenderableHtml(chunkDataText)) {
      const sanitized = sanitizeHtml(chunkDataText);

      // Check if sanitization produced meaningful content
      if (!hasMeaningfulContent(sanitized)) {
        return { hasHtml: false, sanitizedContent: "" };
      }

      return { hasHtml: true, sanitizedContent: sanitized };
    }

    return { hasHtml: false, sanitizedContent: "" };
  }, [chunkDataText]);

  const highlightSearchText = (
    content: string,
    searchText: string | undefined,
  ) => {
    if (!searchText)
      return (
        <pre
          style={{
            margin: 0,
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            fontFamily: "inherit",
          }}
        >
          {content}
        </pre>
      );

    const regex = new RegExp(`(${searchText})`, "gi");

    const parts = content.split(regex);

    return (
      <pre
        style={{
          margin: 0,
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          fontFamily: "inherit",
        }}
      >
        {parts.map((part, index) =>
          part.toLowerCase() === searchText.toLowerCase() ? (
            <Box
              component="span"
              key={index}
              sx={{
                backgroundColor: "#fff59d",
                padding: "2px 4px",
                borderRadius: "3px",
                fontWeight: 600,
                color: "#000",
              }}
            >
              {part}
            </Box>
          ) : (
            <span key={index}>{part}</span>
          ),
        )}
      </pre>
    );
  };

  const isTextSelected = () => {
    const selection = window.getSelection();
    if (!selection) return false;
    return selection.toString().length > 0;
  };

  return (
    <div
      ref={ref}
      id={domId ?? `source-${index}`}
      data-testid={index !== undefined ? `aa-src-item-${index - 1}` : undefined}
    >
      <CCard
        className={
          scrollAndView
            ? "document-chunk-card is-selected"
            : "document-chunk-card"
        }
        style={{
          margin: type === "search" ? "0" : "8px",
          borderRadius: "8px",
          overflow: "hidden",
          cursor: "pointer",
          ...(highlightStyles as React.CSSProperties),
        }}
        onClick={(e) => {
          // prevent the click from firing if they are highlighting the text
          if (isTextSelected()) {
            e.stopPropagation();
            return;
          }
          pageNumberCallback(pageNumbers[0]);
        }}
      >
        <DocumentDataCardHeader
          type={type}
          sdr_id={sdr_id}
          datasetId={datasetId}
          parsedChunkMetadata={parsedChunkMetadata}
          parsedSDRData={parsedSDRData}
          index={index}
        />
        <CCardBody
          style={{
            padding: "20px",
            backgroundColor: "#ffffff",
          }}
        >
          {/* Reference image for image-based chunks (Epic OCR) */}
          {imageArtifactId && (
            <Box sx={{ mb: 2 }}>
              <Chip
                icon={<ImageIcon />}
                label="Source Image"
                size="small"
                variant="outlined"
                sx={{ mb: 1, fontSize: "0.75rem" }}
              />
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={`Page ${pageNumbers?.[0] ?? ""} image`}
                  sx={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: 400,
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    objectFit: "contain",
                    backgroundColor: "#fafafa",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                    color: "#999",
                    fontSize: "0.8rem",
                  }}
                >
                  Loading image...
                </Box>
              )}
            </Box>
          )}
          {isStructuredChunk ? (
            <ChunkContent data={parsedChunkContainer ?? parsedChunkData.data} />
          ) : hasHtml ? (
            <Box
              sx={{
                overflowX: "auto",
                maxWidth: "100%",
                ...htmlContentStyles,
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          ) : searchText ? (
            <div
              style={{
                fontSize: "14px",
                color: "#424242",
                lineHeight: 1.6,
              }}
            >
              {highlightSearchText(chunkDataText, searchText)}
            </div>
          ) : (
            <CollapsibleMarkdown text={chunkDataText} />
          )}
        </CCardBody>
        <CCardFooter
          style={{
            backgroundColor: "#fafafa",
            padding: "14px 20px",
            fontSize: "0.813rem",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "8px",
            }}
          >
            {/* AI-Generated Document Title */}
            {parsedSDRData.extracted_metadata?.title && (
              <div style={{ color: "#333", gridColumn: "1 / -1" }}>
                <strong>Document Title:</strong>{" "}
                <span style={{ color: "#666" }}>
                  {parsedSDRData.extracted_metadata.title}
                </span>
              </div>
            )}

            {/* AI-Generated Author */}
            {parsedSDRData.extracted_metadata?.author && (
              <div style={{ color: "#333" }}>
                <strong>Author:</strong>{" "}
                <span style={{ color: "#666" }}>
                  {parsedSDRData.extracted_metadata.author}
                </span>
              </div>
            )}

            {/* AI-Generated Publication Date */}
            {parsedSDRData.extracted_metadata?.publication_date && (
              <div style={{ color: "#333" }}>
                <strong>Publication Date:</strong>{" "}
                <span style={{ color: "#666" }}>
                  {parsedSDRData.extracted_metadata.publication_date}
                </span>
              </div>
            )}

            {/* File Information */}
            <div style={{ color: "#333" }}>
              <strong>File Name:</strong>{" "}
              <span style={{ color: "#666" }}>{parsedSDRData.file_name}</span>
            </div>

            {/* Page Numbers if available */}
            {/* Backend stores 1-indexed page numbers, use directly */}
            {pageNumbers && pageNumbers.length > 0 && (
              <div style={{ color: "#333" }}>
                <strong>Page(s):</strong>{" "}
                <span style={{ color: "#666" }}>
                  {pageNumbers.length > 1
                    ? `${pageNumbers[0]}-${pageNumbers[pageNumbers.length - 1]}`
                    : pageNumbers[0]}
                </span>
              </div>
            )}
          </div>
        </CCardFooter>
      </CCard>
    </div>
  );
};

export default DocumentDataCard;
